<template>
  <div id="app">
    <!-- 导航栏 - 只在非登录页面显示 -->
    <el-header v-if="showNavbar" class="app-header">
      <div class="header-content">
        <div class="logo">
          <h3>检验报告管理系统</h3>
        </div>
        
    
        <div class="user-info">
          <el-dropdown @command="handleUserCommand">
            <span class="user-name">
              <el-icon><User /></el-icon>
              {{ currentUser }}
              <el-icon class="el-icon--right"><arrow-down /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="logout">
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </el-header>
    
    <!-- 主内容区域 -->
    <div :class="['app-main', { 'with-header': showNavbar }]">
      <router-view />
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getCurrentUser, logout } from './utils/auth.js'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  List, 
  Upload, 
  User, 
  ArrowDown, 
  SwitchButton 
} from '@element-plus/icons-vue'

export default {
  name: 'App',
  components: {
    List,
    Upload,
    User,
    ArrowDown,
    SwitchButton
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    
    const currentUser = ref(getCurrentUser())
    
    // 计算是否显示导航栏 - 只在ReportList页面且用户已登录时显示
    const showNavbar = computed(() => {
      return route.path === '/ReportList' && currentUser.value
    })
    
    // 计算当前激活的菜单项
    const activeIndex = computed(() => {
      if (route.path.startsWith('/view/')) {
        return '/ReportList'
      }
      return route.path
    })
    
    // 监听路由变化，更新用户信息
    watch(() => route.path, () => {
      currentUser.value = getCurrentUser()
    })
    
    // 处理菜单选择
    const handleMenuSelect = (index) => {
      if (index !== route.path) {
        router.push(index)
      }
    }
    
    // 处理用户下拉菜单命令
    const handleUserCommand = async (command) => {
      if (command === 'logout') {
        try {
          await ElMessageBox.confirm(
            '确定要退出登录吗？',
            '提示',
            {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
          
          logout()
          currentUser.value = null
          ElMessage.success('已退出登录')
          router.push('/login')
        } catch {
          // 用户取消操作
        }
      }
    }
    
    return {
      showNavbar,
      activeIndex,
      currentUser,
      handleMenuSelect,
      handleUserCommand
    }
  }
}
</script>

<style>
/* 全局样式重置 */
* {
  box-sizing: border-box;
}

html {
  font-size: 16px;
  /* 设置根字体大小，便于rem单位计算 */
}

body {
  margin: 0;
  padding: 0;
  font-family: 'Avenir', Helvetica, Arial, sans-serif, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #f5f7fa;
  /* 防止触摸缩放但允许正常交互 */
  touch-action: manipulation;
  -webkit-touch-callout: none;
}

#app {
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  /* 防止缩放和触摸操作 */
  touch-action: manipulation;
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
}

/* 导航栏样式 */
.app-header {
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 0;
  height: 60px;
  line-height: 60px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  height: 100%;
}

.logo h3 {
  margin: 0;
  color: #409eff;
  font-size: 18px;
  font-weight: 600;
}

.header-menu {
  flex: 1;
  margin: 0 40px;
  border-bottom: none;
}

.header-menu .el-menu-item {
  height: 60px;
  line-height: 60px;
  border-bottom: none;
}

.header-menu .el-menu-item:hover {
  background-color: #ecf5ff;
}

.header-menu .el-menu-item.is-active {
  border-bottom: 2px solid #409eff;
  color: #409eff;
}

.user-info {
  display: flex;
  align-items: center;
}

.user-name {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s;
  color: #606266;
  font-size: 14px;
}

.user-name:hover {
  background-color: #f5f7fa;
}

.user-name .el-icon {
  margin-right: 4px;
}

.user-name .el-icon--right {
  margin-left: 4px;
  margin-right: 0;
}

/* 主内容区域 */
.app-main {
  flex: 1;
  width: 100%;
}

.app-main.with-header {
  padding-top: 0;
}

/* 移动端适配 */
@media (max-width: 768px) {
  html {
    font-size: 14px;
  }
  
  #app {
    padding: 0;
  }
}

@media (max-width: 480px) {
  html {
    font-size: 12px;
  }
}

/* 通用响应式工具类 */
/* 移除媒体查询，现在使用JavaScript控制显示 */

/* 响应式容器 */
.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

@media (max-width: 768px) {
  .container {
    padding: 0 10px;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 5px;
  }
}
</style>